import { useNavigate, useSearch } from "@tanstack/react-router";
import { useDebitNotes } from "@/api/queries";
import type { DebitNoteSummary } from "@/api/types";
import { PermittedAction } from "@/components/PermittedAction";
import type { Column } from "@/components/list/DataTable";
import { DocumentStatusBadge } from "@/components/list/StatusBadge";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

export function DebitNotesListPage() {
  const search = useSearch({ from: "/purchases/debit-notes" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useDebitNotes(listQueryFor(search));

  const columns: Array<Column<DebitNoteSummary>> = [
    {
      key: "number",
      header: "Number",
      width: "13ch",
      numeric: true,
      render: (row) => row.number ?? "Draft",
    },
    { key: "supplier", header: "Supplier", render: (row) => row.supplierName },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    {
      key: "against",
      header: "Against",
      render: (row) =>
        row.againstNumber ? (
          <span className="num">{row.againstNumber}</span>
        ) : (
          <span className="text-fg-muted">Supplier's account</span>
        ),
    },
    { key: "reason", header: "Reason", render: (row) => row.reason ?? "" },
    {
      key: "status",
      header: "Status",
      width: "14ch",
      render: (row) => <DocumentStatusBadge status={row.status} />,
    },
    {
      key: "total",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.total),
    },
  ];

  return (
    <ListScreen
      route="/purchases/debit-notes"
      title="Debit notes"
      subtitle={data ? `${data.total} this fiscal year` : "Loading debit notes"}
      actions={
        <PermittedAction
          variant="secondary"
          leftIcon="Plus"
          permission="purchases.debitnotes.create"
          onClick={() =>
            void navigate({ to: "/purchases/debit-notes/new", search: { bill: undefined } })
          }
        >
          New debit note
        </PermittedAction>
      }
      columns={columns}
      rows={data?.items ?? []}
      total={data?.total ?? 0}
      hasNext={data?.next != null}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      rowKey={(row) => row.id}
      rowLabel={(row) =>
        `Debit note ${row.number ?? "draft"}, ${row.supplierName}, ${formatAmount(row.total)}`
      }
      onRowClick={(row) =>
        void navigate({ to: "/purchases/debit-notes/$id", params: { id: row.id } })
      }
      searchPlaceholder="Search number, supplier, bill or reason"
      empty={{
        icon: "FilePlus",
        title: "No debit notes yet",
        description: "A debit note reduces what you owe a supplier for a bill already approved.",
      }}
      filteredEmpty={{
        icon: "Search",
        title: "No debit notes match these filters",
        description: "Try a wider date range, or clear the filters to see everything again.",
      }}
    />
  );
}
